/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import moment from 'moment-timezone';
import i18n from 'i18next';
import MarkdownIt from 'markdown-it';

// Component imports
import { Avatar, Button, Col, Dropdown, Form, Grid, Input, List, Menu, Row, Select, Skeleton, Space, Table, Tooltip, Typography } from 'antd';
import { CaretRightOutlined, QuestionOutlined, ReloadOutlined, StarOutlined } from '@ant-design/icons';
import MainActivityButton from '../UI/MainActivityButton';
import DocumentTitle from '../DocumentTitle';
import BotAvatar from '../UI/BotAvatar';
import ObservatoryFiltersRow, { useInitialTenantFilters, useObservatoryFilters, useObservatorySearchParams } from '../UI/ObservatoryFiltersRow';

// Misc imports
import { useFetchActivitiesQuery } from '../../store/activities/activitiesApi';
import { useFetchSurveysQuery, useLazyDownloadSurveysQuery } from '../../store/surveys/surveysApi';
import { revertBlobTypes } from '../../store/utils/revertBlobTypes';
import { ACTIVITY_TYPES, CHAPTERSOURCE_SCHEMAS, DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import is from '../../utils/is';
import handleFileDownload from '../../utils/misc/handleFileDownload';

// Constant declarations
const { useBreakpoint } = Grid;
const md = new MarkdownIt ();

/**
 * Generates a list of available activities, for use within an Select component
 * @param {Array} chaptersources A list of all activities
 * @param {String} primaryBotLanguage The primary language of the bot, to show the correct name of the activity
 * @returns A list of Select options with all available activities
 */
const generateActivitiesFilterContent = (chaptersources = [], primaryBotLanguage, tenantId) => {
  const interviews = chaptersources.filter(c => c.type === ACTIVITY_TYPES.INTERVIEW);
  const liveChatFlows = chaptersources.filter(c => c.type === ACTIVITY_TYPES.LIVECHAT_FLOW);

  let options = [];

  if (is.array(interviews) && !is.emptyArray(interviews)) {
    options = [
      ...options,
      {
        label: i18n.t('Surveys'),
        options: interviews
          .map(activity => {
            return {
              value: activity.id,
              name: activity.name[primaryBotLanguage],
              label: (
                <Typography.Text>
                  <Space size={8}>
                    <MainActivityButton customIndicator={<StarOutlined />} chaptersource={activity} hideIfNotMain botId={tenantId} />
                    {activity.name[primaryBotLanguage]}
                  </Space>
                </Typography.Text>
              )
            };
          })
      }
    ];
  }

  if (is.array(liveChatFlows) && !is.emptyArray(liveChatFlows)) {
    options = [
      ...options,
      {
        label: i18n.t('LiveChat flows'),
        options: liveChatFlows.map(activity => {
          return {
            value: activity.id,
            name: activity.name[primaryBotLanguage],
            label: (
              <Typography.Text>
                <Space size={8}>
                  <MainActivityButton customIndicator={<StarOutlined />} chaptersource={activity} hideIfNotMain botId={tenantId} />
                  {activity.name[primaryBotLanguage]}
                </Space>
              </Typography.Text>
            )
          };
        })
      }
    ];
  }

  // Clean up duplicates, empty groups and flows with no name
  options = options
    .map(group => {
      return {
        ...group,
        options: group.options
          .filter((option, index, self) => {
            return option.name && index === self.findIndex((t) => (t.value === option.value));
          })
      };
    })
    .filter(group => !is.emptyArray(group.options));

  return options;
};

const SurveysTable = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams();
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { primaryBotLanguage } = useBotLanguage();

  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);
  const [expandedRows, setExpandedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: organization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });
  const { data: bots, isFetching: isFetchingBots } = useFetchBotsQuery({ orgId, urlParams: { includeBotDeployments: true } }, { skip: !orgId });

  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const observatorySearchParams = useObservatorySearchParams(orgTimezone);
  const observatoryFilters = useObservatoryFilters(orgTimezone);
  const initialTenantFilters = useInitialTenantFilters();

  const { data: activities, isFetching: isLoadingActivities } = useFetchActivitiesQuery({
    orgId,
    schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH,
    types: `${ACTIVITY_TYPES.INTERVIEW},${ACTIVITY_TYPES.LIVECHAT_FLOW}`,
    urlParams: {
      tenantsFilter: observatorySearchParams.tenantsFilter
    }
  }, { skip: !orgId });
  const [downloadSurveys, { isLoading: isDownloadingSurveys }] = useLazyDownloadSurveysQuery();

  const { data: surveys, isFetching: isLoadingSurveys, refetch: fetchSurveys } = useFetchSurveysQuery({ orgId, urlParams: {
    ...observatorySearchParams,
    timeZone: encodeURIComponent(orgTimezone),
    page: currentPage -1,
    pageSize: pageSize,
    sortBy: searchParams.get('sortBy') || 'createdAt',
    order: searchParams.get('order') || 'desc',
    ...(searchParams.get('separator') && { separator: searchParams.get('separator') }),
    ...(searchParams.get('searchTerm') && { searchTerm: searchParams.get('searchTerm') }),
    ...(searchParams.get('sourceActivityName') && { sourceActivityName: searchParams.get('sourceActivityName') }),
    ...(searchParams.get('type') && { type: searchParams.get('type') })
  } }, {
    skip: !searchParams.get('startDate') || !searchParams.get('endDate'),
    selectFromResult: ({ data }) => {

      let newDataItems = [];
      if(data?.items?.length) {
        newDataItems = data?.items?.map(item => {
          const subscriberName = item?.chatSession?.metadata?.subscriberName || null;
          const subscriberEmail = item?.chatSession?.metadata?.subscriberEmail || null;

          return {
            ...item,
            subscriberName,
            subscriberEmail
          };
        });
      }

      return {
        data: {
          ...data,
          items: newDataItems
        }
      };
    }
  });

  // Memoized values
  const sortingInfo = useMemo(() => {
    let order = searchParams.get('order') ? (searchParams.get('order') === 'desc' ? 'descend' : 'ascend') : 'descend';
    let columnKey = searchParams.get('sortBy') ? searchParams.get('sortBy') : 'createdAt';

    return {
      order,
      columnKey
    };
  }, [searchParams.get('order'), searchParams.get('sortBy')]);

  const columns = [
    {
      title: t('Agent'),
      dataIndex: 'tenantId',
      key: 'tenantId',
      width: '20%',
      render: tenantId => {
        const tenant = (bots?.items || []).find(bot => bot.id === tenantId);

        return isFetchingBots ? (
          <Skeleton.Button style={{ width: 100, height: 16 }} active />
        ) : tenant ? (
          <BotAvatar bot={tenant} avatarSize={16} />
        ) : (
          <Typography.Text type="secondary">{t('(agent not found)')}</Typography.Text>
        );
      }
    },
    {
      title: t('Date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: {
        compare: (a, b) => a.createdAt - b.createdAt
      },
      sortOrder: sortingInfo.columnKey === 'createdAt' && sortingInfo.order,
      render: createdAt => {
        return (
          moment(createdAt).tz(orgTimezone).format(DEFAULT_DATETIME_FORMAT)
        );
      }
    },
    {
      title: t('Created by'),
      key: 'subscriberName',
      responsive: ['xxl'],
      render: record => {
        return record.subscriberName ? (
          <Tooltip
            overlayClassName="pointer-events-none"
            placement="topLeft"
            title={record?.subscriberEmail}
          >
            {record.subscriberName}
          </Tooltip>
        ) : (
          <span className="text-secondary">
            {t('(user unknown)')}
          </span>
        );
      }
    },
    {
      title: t('Session'),
      dataIndex: 'session',
      key: 'session',
      responsive: ['sm']
    }
  ];

  // On mount
  useEffect(() => {
    updateUrlSearchParams(Number(searchParams.get('page')) + 1, DEFAULT_PAGINATION_SIZE, { [sortingInfo.columnKey]: sortingInfo.order === 'descend' ? 'desc' : 'asc' });
  }, [orgTimezone]);

  const onDownload = async () => {
    const urlParams = {
      ...observatorySearchParams,
      timeZone: encodeURIComponent(orgTimezone),
      page: currentPage -1,
      pageSize: pageSize,
      ...(searchParams.get('separator') && { separator: searchParams.get('separator') }),
      sortBy: searchParams.get('sortBy') || 'createdAt',
      order: searchParams.get('order') || 'desc',
      ...(searchParams.get('searchTerm') && { searchTerm: searchParams.get('searchTerm') }),
      ...(searchParams.get('sourceActivityName') && { sourceActivityName: searchParams.get('sourceActivityName') }),
      ...(searchParams.get('type') && { type: searchParams.get('type') })
    };

    const result = await downloadSurveys({ orgId, urlParams }).unwrap();
    const file = revertBlobTypes(result);
    const fileName = file?.headers['content-disposition']?.split(new RegExp(['"'], 'g'))?.[1] ?? 'user_data.csv';
    handleFileDownload(file?.blob, fileName);
  };

  const updateUrlSearchParams = (current = 1, pageSize, sorting = { createdAt: 'desc' }) => {
    setCurrentPage(current);
    if (pageSize) {
      setPageSize(pageSize);
    }
    const urlSearchParams = new URLSearchParams(location.search);

    const separator = form.getFieldValue('separator');
    const sourceActivityName = form.getFieldValue('sourceActivityName');
    const type = form.getFieldValue('type');

    if (separator) {
      urlSearchParams.set('separator', encodeURIComponent(separator));
    } else {
      urlSearchParams.delete('separator');
    }

    if (sourceActivityName) {
      urlSearchParams.set('sourceActivityName', encodeURIComponent(sourceActivityName));
    } else {
      urlSearchParams.delete('sourceActivityName');
    }

    if (type) {
      urlSearchParams.set('type', encodeURIComponent(type));
    } else {
      urlSearchParams.delete('type');
    }

    urlSearchParams.set('page', current - 1);

    if (pageSize) {
      urlSearchParams.set('pageSize', pageSize);
    }

    if (sorting) {
      urlSearchParams.set('sortBy', Object.entries(sorting).flat()[0]);
      urlSearchParams.set('order', Object.entries(sorting).flat()[1]);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const extractContent = (html) => {
    return new DOMParser().parseFromString(html, 'text/html').documentElement.textContent;
  };

  const handleTableChange = (pagination, _filters, sorter) => {
    let sorting;
    if (sorter.column) {
      sorting = { [sorter.columnKey]: sorter.order === 'ascend' ? 'asc' : 'desc' };
    } else {
      if (sorter.field === 'createdAt' && searchParams.get('order') === 'desc') {
        sorting = { [sorter.columnKey]: 'asc' };
      }
    }
    updateUrlSearchParams(pagination.current, pagination.pageSize, sorting);
  };

  return (
    <Fragment>
      <DocumentTitle title={t('Surveys')} />

      <Form
        layout={isMobileOrTabletSize ? 'vertical' : 'inline'}
        name="surveyFiltersForm"
        initialValues={{
          tenantsFilter: initialTenantFilters,
          rangePicker: [observatoryFilters.startDate, observatoryFilters.endDate],
          sourceActivityName: searchParams.get('sourceActivityName') ? decodeURIComponent(searchParams.get('sourceActivityName')) : null,
          type: searchParams.get('type') ? decodeURIComponent(searchParams.get('type')) : null,
          separator: ','
        }}
        form={form}
        onFinish={onDownload}
      >
        <div className="w-100">
          <ObservatoryFiltersRow
            form={form}
            rangePickerProps={{
              disabled: isLoadingSurveys || isDownloadingSurveys,
              timezone: orgTimezone
            }}
            agentsPickerProps={{
              agentsList: bots?.items || [],
              disabled: isLoadingSurveys || isDownloadingSurveys,
              loading: isFetchingBots
            }}
          />

          <Row gutter={24}>
            <Col flex="auto">
              <Row gutter={[8, 8]} wrap={false}>
                <Col
                  flex="auto"
                  className={classNames({
                    'max-field-width': !isMobileOrTabletSize
                  })}
                >
                  <Form.Item
                    name="sourceActivityName"
                    noStyle
                  >
                    <Select
                      size="large"
                      allowClear
                      placeholder={t('Filter by flow')}
                      showSearch
                      style={{ minWidth: 200 }}
                      disabled={isLoadingActivities}
                      loading={isLoadingActivities}
                      onChange={() => updateUrlSearchParams()}
                      dropdownMatchSelectWidth={false}
                      filterOption={(input, option) =>
                        (activities || []).find(activity => activity.id === option.value)?.name[primaryBotLanguage].toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      className="w-100"
                    >
                      {
                        generateActivitiesFilterContent(activities || [], primaryBotLanguage, null).map(group => (
                          <Select.OptGroup key={group.label} label={group.label}>
                            {
                              group.options.map(opt => (
                                <Select.Option
                                  key={opt.value}
                                  value={opt.value}
                                  name={opt.name}
                                >
                                  {opt.label}
                                </Select.Option>
                              ))
                            }
                          </Select.OptGroup>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col flex="none" className={classNames({ 'mt-2': isMobileOrTabletSize })}>
              <Row gutter={[8, 8]} justify={isMobileOrTabletSize ? 'start' : 'end'} wrap={false}>
                <Col flex="none">
                  <Dropdown.Button
                    size="large"
                    disabled={isDownloadingSurveys}
                    overlay={
                      <Menu
                        onClick={(event) => {
                          form.setFieldsValue({ separator: event.key }); updateUrlSearchParams();
                        }}
                      >
                        <Menu.Item key=",">
                          {
                            form.getFieldValue('separator') === ',' ? (
                              <CaretRightOutlined className="mr-1" />
                            ) : (
                              <span className="pl-5"></span>
                            )
                          }
                          {t('Comma separator')} (,)
                        </Menu.Item>

                        <Menu.Item key=";">
                          {
                            form.getFieldValue('separator') === ';' ? (
                              <CaretRightOutlined className="mr-1" />
                            ) : (
                              <span className="pl-5"></span>
                            )
                          }
                          {t('Semicolon separator')} (;)
                        </Menu.Item>
                      </Menu>
                    }
                    onClick={onDownload}
                    className={classNames({
                      'w-100': isMobileOrTabletSize
                    })}
                  >
                    {t('Download')}
                  </Dropdown.Button>

                  <Form.Item
                    name="separator"
                    hidden
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col flex="none">
                  <Button
                    size="large"
                    type="primary"
                    disabled={isLoadingSurveys}
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      setExpandedRows([]);
                      fetchSurveys();
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </Form>

      <Row gutter={24} className="mt-2">
        <Col xs={24}>
          <Table
            size="small"
            rowKey="id"
            columns={columns}
            onChange={handleTableChange}
            pagination={{
              size: 'default',
              total: surveys?.total || 0,
              defaultCurrent: 1,
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total) => `${t('Total Surveys')}: ${total}`
            }}
            rowClassName="cursor-pointer"
            onRow={(record) => ({
              onClick: () => {
                if(expandedRows.includes(record?.id)) {
                  setExpandedRows(
                    expandedRows.filter( row => {
                      return row !== record.id;
                    })
                  );
                } else {
                  setExpandedRows( state => ([
                    ...state,
                    record.id
                  ]));
                }
              }
            })}
            dataSource={surveys && is.array(surveys.items) && surveys.items}
            loading={isLoadingSurveys}
            expandedRowKeys={expandedRows}
            expandable={{
              onExpand: (expanded, record) => {
                if (expanded) {
                  setExpandedRows( state => ([
                    ...state,
                    record.id
                  ]));
                } else {
                  setExpandedRows(
                    expandedRows.filter( row => {
                      return row !== record.id;
                    })
                  );
                }
              },
              expandedRowRender: record => (
                <List
                  itemLayout="horizontal"
                  dataSource={record.questions}
                  renderItem={item => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar size={27} icon={<QuestionOutlined />} />}
                        title={extractContent(md.render(item.question))}
                        description={item.answer}
                      />
                    </List.Item>
                  )}
                />
              ),
              rowExpandable: _record => true
            }}
          />
        </Col>
      </Row>
    </Fragment>
  );
};

export default SurveysTable;
